import {
  Button as ChakraButton,
  IconButton as ChakraIconButton,
} from "@chakra-ui/react";
import { forwardRef } from "react";

/**
 * Locked button sizes for the design system:
 *   - "sm" → 32px tall, 12px text. Inline / table actions.
 *   - "md" → 40px tall, 14px text. Default — every form, every page header.
 *   - "lg" → 48px tall, 16px text. Primary marketing / onboarding CTAs.
 *
 * **Use `intent` instead of raw variant/colorPalette for consistency.**
 *
 * | Intent      | Use for                              | Visual                            |
 * |-------------|--------------------------------------|-----------------------------------|
 * | "primary"   | Save, Submit, Create, Add, Confirm   | Solid Electric Blue               |
 * | "secondary" | Cancel, Back, Refresh, secondary CTA | Outline, neutral border           |
 * | "danger"    | Delete, Remove, destructive confirm  | Solid red                         |
 * | "ghost"     | Inline icon-only / minor row actions | Ghost (no bg, no border)          |
 *
 * Picking `intent` ensures every "Save" looks identical to every other "Save"
 * across the whole app. Don't reach for `colorPalette` / `variant` directly
 * unless you have a genuinely off-spec button.
 *
 * Use the `mono` flag for editorial action labels ("Refresh", "Export") —
 * applies Geist Mono UPPERCASE 0.10em tracking. Pairs naturally with intent.
 */

export type ButtonIntent = "primary" | "secondary" | "danger" | "ghost";

interface ButtonProps extends React.ComponentProps<typeof ChakraButton> {
  children?: React.ReactNode;
  /** Semantic intent — the canonical way to pick a button style. */
  intent?: ButtonIntent;
  /** Editorial mono treatment: Geist Mono, uppercase, tracked. */
  mono?: boolean;
}

// Brand spec: Geist Mono Medium / 1.40px tracking (= 0.10em at 14px).
// Buttons use 12px so the proportion is slightly tighter (1.20px) — this avoids
// the label feeling looser than the surrounding pre-header.
const monoStyles = {
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.10em",
  fontWeight: 500,
  fontSize: "12px",
};

const intentStyles: Record<
  ButtonIntent,
  { variant: string; colorPalette: string }
> = {
  primary: { variant: "solid", colorPalette: "blue" },
  secondary: { variant: "outline", colorPalette: "gray" },
  danger: { variant: "solid", colorPalette: "red" },
  ghost: { variant: "ghost", colorPalette: "gray" },
};

// Chakra v3's outline variant pulls borderColor from `colors.colorPalette.border`,
// which is near-invisible for the gray palette and harshly saturated for blue/red.
// We override at the wrapper level so every secondary button shows an identical
// soft border (and outline buttons routed via plain `variant="outline"` get the
// same treatment).
const outlineBorderOverride = {
  borderColor: "var(--card-border)",
  _hover: { borderColor: "var(--border-default)" },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ intent, mono, variant, colorPalette, children, ...props }, ref) => {
    // intent maps to variant + colorPalette; explicit props on the call site
    // still win, so a one-off can override.
    const intentResolved = intent ? intentStyles[intent] : undefined;
    const finalVariant = variant ?? intentResolved?.variant;
    const finalColorPalette = colorPalette ?? intentResolved?.colorPalette;
    const isOutline = finalVariant === "outline";
    return (
      <ChakraButton
        ref={ref}
        variant={finalVariant as any}
        colorPalette={finalColorPalette as any}
        {...(isOutline ? outlineBorderOverride : {})}
        {...(mono ? monoStyles : {})}
        {...props}
      >
        {children}
      </ChakraButton>
    );
  },
);

Button.displayName = "Button";

interface IconButtonProps extends React.ComponentProps<
  typeof ChakraIconButton
> {}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, ref) => {
    return <ChakraIconButton ref={ref} {...props} />;
  },
);

IconButton.displayName = "IconButton";
